import { Global as EmotionGlobal, css } from '@emotion/react';
import { t } from 'i18n';
import Carousel, { ControlProps, ScrollMode } from 'nuka-carousel';
import { PropsWithChildren } from 'react';

import { DeviceType, useMaxDeviceWidth } from '../../hooks';
import { SasIconButton } from '../SasIcon';
import { theme } from '../theme';

const styles = {
  button: (isLeft: boolean) => css`
    cursor: pointer;
    margin-left: ${isLeft ? '-4px' : 'auto'};
    margin-right: ${isLeft ? 'auto' : '-4px'};
    width: 32px;
    height: 32px;
    min-width: 32px;
    min-height: 32px;
    background-color: #003df0;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover,
    &:focus {
      background-color: #003df0;
    }

    svg {
      rotate: ${isLeft ? '-90deg' : '90deg'};
    }
  `,
  codeShareContainer: css`
    margin: ${theme.spacing.md} ${theme.spacing.sm} 0 ${theme.spacing.sm};
    padding: ${theme.spacing.md};
    background-color: ${theme.colors.white};
    border-radius: ${theme.borders.radius.sm};
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing.sm};
  `,
  codeShareHeaderText: css`
    gap: ${theme.spacing.sm};
    flex-direction: column;
    display: flex;
  `,
  codeShareHeader: css`
    font-weight: 700;
  `,
  codeShareText: css``,
};

const CarouselPrevButton = ({ previousDisabled, previousSlide }: ControlProps) => {
  if (previousDisabled) return null;
  return (
    <SasIconButton
      onClick={() => previousSlide()}
      css={styles.button(true)}
      name="cl--arrow-up"
      color={theme.colors.white}
      hitArea={0}
    />
  );
};

const CarouselNextButton = ({ nextDisabled, nextSlide }: ControlProps) => {
  if (nextDisabled) return null;
  return (
    <SasIconButton
      onClick={() => nextSlide()}
      css={styles.button(false)}
      name="cl--arrow-up"
      color={theme.colors.white}
      hitArea={0}
    />
  );
};

const getSlidesToShow = (isSmall: boolean, isMedium: boolean): number => {
  if (isSmall) return 1;
  if (isMedium) return 3;
  return 4;
};

const InfoIcon = () => {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ flexShrink: 0 }}
    >
      <path
        d="M12.0003 16.75C12.2129 16.75 12.391 16.6781 12.5345 16.5343C12.6782 16.3906 12.75 16.2125 12.75 16V11.75C12.75 11.5375 12.6781 11.3593 12.5343 11.2155C12.3904 11.0718 12.2122 11 11.9998 11C11.7871 11 11.609 11.0718 11.4655 11.2155C11.3218 11.3593 11.25 11.5375 11.25 11.75V16C11.25 16.2125 11.3219 16.3906 11.4658 16.5343C11.6096 16.6781 11.7878 16.75 12.0003 16.75ZM12 9.2885C12.2288 9.2885 12.4207 9.21108 12.5755 9.05625C12.7303 8.90142 12.8077 8.70958 12.8077 8.48075C12.8077 8.25192 12.7303 8.06008 12.5755 7.90525C12.4207 7.75058 12.2288 7.67325 12 7.67325C11.7712 7.67325 11.5793 7.75058 11.4245 7.90525C11.2697 8.06008 11.1923 8.25192 11.1923 8.48075C11.1923 8.70958 11.2697 8.90142 11.4245 9.05625C11.5793 9.21108 11.7712 9.2885 12 9.2885ZM12.0017 21.5C10.6877 21.5 9.45267 21.2507 8.2965 20.752C7.14033 20.2533 6.13467 19.5766 5.2795 18.7218C4.42433 17.8669 3.74725 16.8617 3.24825 15.706C2.74942 14.5503 2.5 13.3156 2.5 12.0017C2.5 10.6877 2.74933 9.45267 3.248 8.2965C3.74667 7.14033 4.42342 6.13467 5.27825 5.2795C6.13308 4.42433 7.13833 3.74725 8.294 3.24825C9.44967 2.74942 10.6844 2.5 11.9983 2.5C13.3123 2.5 14.5473 2.74933 15.7035 3.248C16.8597 3.74667 17.8653 4.42342 18.7205 5.27825C19.5757 6.13308 20.2528 7.13833 20.7518 8.294C21.2506 9.44967 21.5 10.6844 21.5 11.9983C21.5 13.3123 21.2507 14.5473 20.752 15.7035C20.2533 16.8597 19.5766 17.8653 18.7218 18.7205C17.8669 19.5757 16.8617 20.2528 15.706 20.7518C14.5503 21.2506 13.3156 21.5 12.0017 21.5ZM12 20C14.2333 20 16.125 19.225 17.675 17.675C19.225 16.125 20 14.2333 20 12C20 9.76667 19.225 7.875 17.675 6.325C16.125 4.775 14.2333 4 12 4C9.76667 4 7.875 4.775 6.325 6.325C4.775 7.875 4 9.76667 4 12C4 14.2333 4.775 16.125 6.325 17.675C7.875 19.225 9.76667 20 12 20Z"
        fill="#005C8A"
      />
    </svg>
  );
};

type Props = PropsWithChildren<{
  isCodeShare?: boolean;
  className?: string;
}>;

export function FlightProductCarousel({ className, children, isCodeShare = false }: Props) {
  const isSmall = useMaxDeviceWidth(DeviceType.Tablet);
  const isMedium = useMaxDeviceWidth(DeviceType.Laptop);

  return (
    <div className={className}>
      <CarouselOverrideStyles />
      <Carousel
        slidesToShow={getSlidesToShow(isSmall, isMedium)}
        scrollMode={ScrollMode.page}
        cellSpacing={4}
        defaultControlsConfig={{
          pagingDotsStyle: { display: 'none' },
        }}
        renderCenterLeftControls={CarouselPrevButton}
        renderCenterRightControls={CarouselNextButton}
        style={{
          height: '100%',
        }}
      >
        {children}
      </Carousel>
      {isCodeShare && (
        <div css={styles.codeShareContainer}>
          <InfoIcon />
          <div css={styles.codeShareHeaderText}>
            <div css={styles.codeShareHeader}>
              {' '}
              {t('common:partnerAirlinesDisclaimerHeader', {
                defaultValue: 'Your trip includes one or more partner airlines',
              })}
            </div>
            <div css={styles.codeShareText}>
              {t('common:partnerAirlinesDisclaimer', {
                defaultValue:
                  'Products and services may differ from those offered by SAS – such as baggage rules and onboard service.',
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const overrideCssStyles = `
  .slider-container {
    height: 100%;
  }

  .slider-container .slider-frame .slider-list {
    height: 100%;
  }

  .slide {
    display: flex;
  }
`;

const CarouselOverrideStyles = () => {
  return <EmotionGlobal styles={css(overrideCssStyles)} />;
};
